import { useTranslation } from "react-i18next";
import { useQuery } from "@tanstack/react-query";
import SectionHeading from "../components/SectionHeading";
import AboutUsDescription from "../components/AboutUsDescription";
import { useInternationalizedItems } from "../custom-hooks/useInternationalizedItems";
import { HeadingVariant } from "../types/constants";
import TimelineItem from "./TimelineItem";
import { developmentHistoryServiceFactory } from "../services/DevelopmentHistoryServiceImpl";
import { IDevelopmentHistoryFlat } from "../domain/models/DevelopmentHistory";

type ParagraphText =
    | string
    | {
        before: string;
        highlight: string;
        after: string;
    };

const developmentHistoryService = developmentHistoryServiceFactory();

export default function HistorySection() {
    const { t, i18n } = useTranslation();

    const paragraphs = useInternationalizedItems<ParagraphText[]>({
        key: "about.paragraphs.developmentHistory",
    });

    const { data: developmentHistory = [] } = useQuery({
        queryKey: ["developmentHistory", 1, 100, i18n.language],
        queryFn: async () => {
            const { rows } = await developmentHistoryService.getAllDevelopmentHistory(1, 100);
            return rows;
        },
    });

    const sortedHistoryItems: IDevelopmentHistoryFlat[] = [...developmentHistory].sort((a, b) => {
        if (!a.createdAt || !b.createdAt) return 0;
        return new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime();
    });

    if (!sortedHistoryItems.length) {
        return null;
    }

    return (
        <section>
            <div className="flex flex-col gap-[32px] md:gap-[42px] lg:gap-[50px] items-start">
                <div className="flex flex-col w-full lg:w-[630px]">
                    <div className="mb-[10px]">
                        <SectionHeading text={t("about.headings.developmentHistory")} variant={HeadingVariant.DARK} />
                    </div>
                    <div>
                        <AboutUsDescription paragraphs={paragraphs} />
                    </div>
                </div>
                <div className="relative flex flex-col gap-[80px] w-full">
                    <div className="absolute top-0 h-full w-[2px] bg-[#1750A4] rounded-full left-0 lap:left-1/2 lap:-translate-x-1/2"></div>
                    {sortedHistoryItems.map((item, index) => (
                        <div key={item.id} className="relative">
                            <div className="absolute w-[14px] h-[14px] bg-[#1750A4] rounded-full z-10 left-[-5px] lap:left-1/2 lap:-translate-x-1/2"></div>
                            <TimelineItem
                                title={item.title}
                                description={item.description}
                                stageDateTime={item.stageDateTime}
                                imageUrl={item.pictureExists?.() ? item.getPictureUrl?.() || undefined : undefined}
                                index={index}
                            />
                        </div>
                    ))}
                </div>
            </div>
        </section>
    );
}
